/**
 * PublicProfile - Shareable travel profile page
 * Features: Dark mode support, World Map visualization, Badges, Streak
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RankThemeProvider } from "@/contexts/RankThemeContext";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import {
  Globe,
  MapPin,
  Trophy,
  Calendar,
  Share2,
  ArrowLeft,
  Loader2,
  Plane,
  CheckCircle2,
  Copy,
  Check,
  Crown,
  Flame,
  Award,
  MessageCircle,
  Camera,
  Heart,
  Send,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Image,
  X,
  UserPlus,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FoundingBadgeInline } from "@/components/FoundingBadge";

import { BACKEND_URL as API_URL } from "@/config/api";
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Category colors for dark mode support
const CATEGORY_COLORS = {
  travel: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  experiences: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
  adventure: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
  food: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  romance: "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300",
  goals: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300",
};

// Tier colors for achievement badges
const TIER_COLORS = {
  bronze: "from-amber-600 to-amber-400",
  silver: "from-gray-400 to-gray-200",
  gold: "from-yellow-500 to-yellow-300",
  platinum: "from-cyan-400 to-cyan-200",
  diamond: "from-purple-500 to-pink-400",
};

// Country name to ISO code mapping for the map
const COUNTRY_NAME_TO_ID = {
  "US": "840", "USA": "840", "United States": "840",
  "GB": "826", "UK": "826", "United Kingdom": "826",
  "FR": "250", "France": "250",
  "DE": "276", "Germany": "276",
  "IT": "380", "Italy": "380",
  "ES": "724", "Spain": "724",
  "JP": "392", "Japan": "392",
  "CN": "156", "China": "156",
  "AU": "036", "Australia": "036",
  "BR": "076", "Brazil": "076",
  "CA": "124", "Canada": "124",
  "MX": "484", "Mexico": "484",
  "IN": "356", "India": "356",
  "RU": "643", "Russia": "643",
  "ZA": "710", "South Africa": "710",
  "EG": "818", "Egypt": "818",
  "TH": "764", "Thailand": "764",
  "GR": "300", "Greece": "300",
  "PT": "620", "Portugal": "620",
  "NL": "528", "Netherlands": "528",
  "SE": "752", "Sweden": "752",
  "JM": "388", "Jamaica": "388",
  "KE": "404", "Kenya": "404",
  "AR": "032", "Argentina": "032",
  "CL": "152", "Chile": "152",
  "PE": "604", "Peru": "604",
  "CO": "170", "Colombia": "170",
  "NZ": "554", "New Zealand": "554",
  "IE": "372", "Ireland": "372",
  "NO": "578", "Norway": "578",
  "DK": "208", "Denmark": "208",
  "FI": "246", "Finland": "246",
  "PL": "616", "Poland": "616",
  "AT": "040", "Austria": "040",
  "CH": "756", "Switzerland": "756",
  "BE": "056", "Belgium": "056",
  "CZ": "203", "Czech Republic": "203", "Czechia": "203",
  "HU": "348", "Hungary": "348",
  "TR": "792", "Turkey": "792", "Türkiye": "792",
  "ID": "360", "Indonesia": "360",
  "MY": "458", "Malaysia": "458",
  "SG": "702", "Singapore": "702",
  "PH": "608", "Philippines": "608",
  "VN": "704", "Vietnam": "704",
  "KR": "410", "South Korea": "410",
  "AE": "784", "UAE": "784", "United Arab Emirates": "784",
  "SA": "682", "Saudi Arabia": "682",
  "IL": "376", "Israel": "376",
  "MA": "504", "Morocco": "504",
  "NG": "566", "Nigeria": "566",
  "GH": "288", "Ghana": "288",
  "TZ": "834", "Tanzania": "834",
  "CR": "188", "Costa Rica": "188",
  "PA": "591", "Panama": "591",
  "CU": "192", "Cuba": "192",
  "DO": "214", "Dominican Republic": "214",
  "PR": "630", "Puerto Rico": "630",
  "IS": "352", "Iceland": "352",
  "HR": "191", "Croatia": "191",
  "RO": "642", "Romania": "642",
  "BG": "100", "Bulgaria": "100",
  "UA": "804", "Ukraine": "804",
};

const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [useGoldTheme, setUseGoldTheme] = useState(true); // Founders can toggle their theme
  const [mapPosition, setMapPosition] = useState({ coordinates: [0, 30], zoom: 1 });
  
  // Check if viewing own profile
  const isOwnProfile = user?.preferences?.username?.toLowerCase() === username?.toLowerCase();
  
  // Social interactions state
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState({});
  const [newComment, setNewComment] = useState("");
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [authorName, setAuthorName] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showComments, setShowComments] = useState(false);
  
  // Sort order state
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first
  
  // Selected trip for detailed view
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripComments, setTripComments] = useState([]);
  const [tripReactions, setTripReactions] = useState({});
  const [newTripComment, setNewTripComment] = useState("");
  const [tripCommentAuthor, setTripCommentAuthor] = useState("");
  const [submittingTripComment, setSubmittingTripComment] = useState(false);

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followToggling, setFollowToggling] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/profile/public/${username}`);
        setProfile(response.data);
        
        // Fetch comments and reactions
        const [commentsRes, reactionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/profile/public/${username}/comments`).catch(() => ({ data: { comments: [] } })),
          axios.get(`${API_URL}/api/profile/public/${username}/reactions`).catch(() => ({ data: { reactions: {} } }))
        ]);
        setComments(commentsRes.data.comments || []);
        setReactions(reactionsRes.data.reactions || {});

        // Load follow status (best-effort — only auth users see this)
        try {
          const fs = await axios.get(`${API_URL}/api/follows/${username}/status`);
          setIsFollowing(!!fs.data?.following);
          setFollowerCount(fs.data?.follower_count || 0);
        } catch (e) {
          // Anonymous viewers won't have auth → just fetch follower count
          try {
            const fl = await axios.get(`${API_URL}/api/follows/${username}/followers?limit=1`);
            setFollowerCount(fl.data?.count || 0);
          } catch (_) {}
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Profile not found");
        } else {
          setError("Failed to load profile");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  // Convert country codes to numeric IDs for the map
  const visitedCountryIds = useMemo(() => {
    if (!profile?.visited_country_codes) return new Set();
    
    const ids = new Set();
    profile.visited_country_codes.forEach(code => {
      const numericId = COUNTRY_NAME_TO_ID[code] || COUNTRY_NAME_TO_ID[code?.toUpperCase()];
      if (numericId) {
        ids.add(numericId);
      }
    });
    return ids;
  }, [profile?.visited_country_codes]);

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.name}'s Travel Profile - DuoLyst`,
          text: `Check out ${profile.name}'s bucket list adventures!`,
          url,
        });
      } catch (err) {
        console.debug("Share cancelled:", err.message);
      }
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Profile link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Track user's reaction in localStorage (one per profile/trip)
  const [myProfileReaction, setMyProfileReaction] = useState(() => {
    return localStorage.getItem(`duolyst_reaction_profile_${username}`) || null;
  });

  // Follow / unfollow this profile
  const handleToggleFollow = async () => {
    if (!user) {
      toast.info("Sign in to follow this profile");
      navigate("/login");
      return;
    }
    if (isOwnProfile) return;
    setFollowToggling(true);
    const next = !isFollowing;
    setIsFollowing(next);
    setFollowerCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      if (next) {
        await axios.post(`${API_URL}/api/follows/${username}`);
        toast.success(`Following ${profile?.name || "@" + username}`);
      } else {
        await axios.delete(`${API_URL}/api/follows/${username}`);
        toast.success("Unfollowed");
      }
    } catch (err) {
      // revert optimistic state on failure
      setIsFollowing(!next);
      setFollowerCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(err.response?.data?.detail || "Failed to update follow status");
    } finally {
      setFollowToggling(false);
    }
  };

  // Add reaction to profile (one per user)
  const handleReaction = async (emoji) => {
    // If clicking same emoji, do nothing (already reacted)
    if (myProfileReaction === emoji) {
      toast.info("You've already reacted with this emoji!");
      return;
    }
    
    try {
      // If had previous reaction, we'll replace it
      const res = await axios.post(`${API_URL}/api/profile/public/${username}/reactions`, { 
        emoji,
        previous_emoji: myProfileReaction 
      });
      setReactions(res.data.reactions);
      setMyProfileReaction(emoji);
      localStorage.setItem(`duolyst_reaction_profile_${username}`, emoji);
      toast.success("Thanks for the reaction!");
    } catch (err) {
      toast.error("Failed to add reaction");
    }
  };

  // Add comment to profile
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    
    setSubmittingComment(true);
    try {
      const res = await axios.post(`${API_URL}/api/profile/public/${username}/comments`, {
        text: newComment.trim(),
        author_name: authorName.trim() || "Anonymous"
      });
      setComments([res.data, ...comments]);
      setNewComment("");
      toast.success("Comment added!");
    } catch (err) {
      console.error("Comment error:", err);
      toast.error(err.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Track user's reaction per trip
  const getMyTripReaction = (tripId) => {
    return localStorage.getItem(`duolyst_reaction_trip_${tripId}`) || null;
  };

  // Load trip details with comments/reactions
  const handleTripClick = async (trip) => {
    setSelectedTrip(trip);
    try {
      const [commentsRes, reactionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/profile/public/${username}/trips/${trip.id}/comments`).catch(() => ({ data: { comments: [] } })),
        axios.get(`${API_URL}/api/profile/public/${username}/trips/${trip.id}/reactions`).catch(() => ({ data: { reactions: {} } }))
      ]);
      setTripComments(commentsRes.data.comments || []);
      setTripReactions(reactionsRes.data.reactions || {});
    } catch (err) {
      console.error("Failed to load trip details");
    }
  };

  // Add comment to a trip
  const handleAddTripComment = async (e) => {
    e.preventDefault();
    if (!newTripComment.trim() || !selectedTrip) return;
    setSubmittingTripComment(true);
    try {
      const res = await axios.post(`${API_URL}/api/profile/public/${username}/trips/${selectedTrip.id}/comments`, {
        text: newTripComment.trim(),
        author_name: tripCommentAuthor.trim() || "Anonymous"
      });
      setTripComments(prev => [...prev, res.data]);
      setNewTripComment("");
      toast.success("Comment added!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmittingTripComment(false);
    }
  };

  // Add reaction to trip (one per user per trip)
  const handleTripReaction = async (tripId, emoji) => {
    const myReaction = getMyTripReaction(tripId);
    
    if (myReaction === emoji) {
      toast.info("You've already reacted with this emoji!");
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/profile/public/${username}/trips/${tripId}/reactions`, { 
        emoji,
        previous_emoji: myReaction
      });
      setTripReactions(res.data.reactions);
      localStorage.setItem(`duolyst_reaction_trip_${tripId}`, emoji);
    } catch (err) {
      toast.error("Failed to add reaction");
    }
  };

  // Sort adventures
  const sortedAdventures = useMemo(() => {
    if (!profile?.recent_adventures) return [];
    const sorted = [...profile.recent_adventures];
    sorted.sort((a, b) => {
      const dateA = new Date(a.target_date || a.completed_at);
      const dateB = new Date(b.target_date || b.completed_at);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [profile?.recent_adventures, sortOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Globe className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-serif font-semibold text-foreground mb-2">
            {error}
          </h1>
          <p className="text-muted-foreground mb-6">
            This profile may be private or doesn't exist.
          </p>
          <Link to="/">
            <Button className="bg-primary hover:bg-primary/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get rank color classes for dynamic styling
  const rankColor = profile?.travel_rank?.color || "from-green-400 to-green-600";
  const rankTextColor = profile?.travel_rank?.textColor || "text-green-500";

  return (
    <RankThemeProvider rankName={profile?.travel_rank?.name} useRankTheme={true}>
    <div className="min-h-screen bg-background">
      {/* Header with Rank-themed accent */}
      <header className="bg-card/80 backdrop-blur-sm border-b border-border sticky top-0 z-40">
        <div className={`h-1 bg-gradient-to-r ${rankColor}`} />
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="DuoLyst" className="h-16 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            {user && username !== user?.preferences?.username && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                Dashboard
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleShare}>
              {copied ? (
                <Check className="w-4 h-4 mr-2 text-green-500" />
              ) : (
                <Share2 className="w-4 h-4 mr-2" />
              )}
              Share
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6 relative overflow-hidden">
          {/* Rank-themed gradient accent */}
          <div className={`absolute top-0 left-0 right-0 h-24 bg-gradient-to-r ${rankColor} opacity-10`} />
          
          <div className="relative flex items-start gap-4">
            {/* Avatar with rank-colored ring */}
            <div className={`w-20 h-20 rounded-full p-1 bg-gradient-to-br ${rankColor} shadow-lg ${profile.streak_frame_enabled ? 'streak-flame-frame' : ''}`}>
              {profile.profile_picture_path || profile.avatar_url ? (
                <img 
                  src={profile.profile_picture_path 
                    ? `${API_URL}/api/profile/picture/${profile.profile_picture_path}`
                    : profile.avatar_url
                  } 
                  alt={profile.name} 
                  className="w-full h-full rounded-full object-cover bg-card"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-card flex items-center justify-center text-2xl font-bold text-foreground">
                  {profile.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold text-foreground">
                  {profile.name}
                </h1>
                {/* Founding Explorer Badge */}
                {profile.founding_explorer && (
                  <FoundingBadgeInline 
                    founderNumber={profile.founding_explorer_number}
                    size="xs"
                    showNumber={true}
                  />
                )}
                {/* Legend Title */}
                {profile.streak_legend_title && (
                  <span className="text-xs px-2 py-0.5 bg-pink-500/20 text-pink-500 rounded-full font-medium">Legend</span>
                )}
                {/* Ambassador Badge */}
                {profile.ambassador_level && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    profile.ambassador_level === "gold" ? "bg-yellow-500/20 text-yellow-600" :
                    profile.ambassador_level === "silver" ? "bg-slate-300/20 text-slate-500" :
                    "bg-amber-500/20 text-amber-600"
                  }`}>{profile.ambassador_level === "gold" ? "Gold" : profile.ambassador_level === "silver" ? "Silver" : ""} Ambassador</span>
                )}
              </div>
              <p className="text-muted-foreground">@{profile.username}</p>
              {profile.bio && (
                <p className="text-foreground/80 mt-2">{profile.bio}</p>
              )}
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                Member since {profile.member_since}
              </div>

              {/* Follow + follower count row */}
              <div className="flex items-center gap-3 mt-3" data-testid="follow-row">
                {!isOwnProfile && (
                  <Button
                    onClick={handleToggleFollow}
                    disabled={followToggling}
                    size="sm"
                    className={`rounded-full px-4 h-8 text-sm font-semibold ${
                      isFollowing
                        ? "bg-muted text-foreground hover:bg-muted/80 border border-border"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                    data-testid="follow-btn"
                  >
                    {isFollowing ? (
                      <>
                        <UserCheck className="w-4 h-4 mr-1.5" />
                        Following
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-1.5" />
                        Follow
                      </>
                    )}
                  </Button>
                )}
                <span className="text-xs text-muted-foreground" data-testid="follower-count">
                  <span className="font-semibold text-foreground">{followerCount}</span>
                  {" "}follower{followerCount === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Rank Card */}
        {profile.travel_rank && (
          <div className={`bg-gradient-to-r ${rankColor} rounded-2xl p-5 shadow-lg mb-6 text-white`} data-testid="travel-rank-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl shadow-lg">
                  {profile.travel_rank.emoji}
                </div>
                <div>
                  <h3 className="font-extrabold text-xl drop-shadow-md">
                    {profile.travel_rank.name}
                  </h3>
                  <p className="text-white/80 text-sm">Travel Rank</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  {profile.stats?.world_percentage || 0}%
                </div>
                <p className="text-white/80 text-sm">World Explored</p>
              </div>
            </div>
          </div>
        )}

        {/* World Map Visualization */}
        {profile.visited_country_codes && profile.visited_country_codes.length > 0 && (
          <div className={`rounded-2xl p-4 shadow-lg border mb-6 overflow-hidden ${
            (profile.founding_explorer && useGoldTheme)
              ? "bg-[#0a0a0f] border-amber-500/40 shadow-amber-500/10" 
              : "bg-card border-border"
          }`} data-testid="world-map-section">
            <div className="flex items-center justify-between mb-3 px-2">
              <h2 className={`font-semibold flex items-center gap-2 ${
                (profile.founding_explorer && useGoldTheme) ? "text-amber-400" : "text-foreground"
              }`}>
                <Globe className={`w-5 h-5 ${(profile.founding_explorer && useGoldTheme) ? "text-amber-400" : rankTextColor}`} />
                World Map ({profile.stats?.countries_visited || 0} countries)
              </h2>
              {/* Founder Theme Toggle */}
              {profile.founding_explorer && (
                <button
                  onClick={() => setUseGoldTheme(!useGoldTheme)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    useGoldTheme 
                      ? "bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-amber-300 border-amber-500/30 hover:border-amber-400/50" 
                      : "bg-muted text-muted-foreground border-border hover:border-primary/30"
                  }`}
                >
                  {useGoldTheme ? "✨ Founder's Gold Theme" : "Default Theme"}
                </button>
              )}
            </div>
            <div 
              className={`rounded-xl overflow-hidden ${(profile.founding_explorer && useGoldTheme) ? "bg-[#0d0d12]" : "bg-muted/30"}`}
              style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                  scale: 120,
                  center: [0, 30],
                }}
                style={{ width: "100%", height: "auto" }}
              >
                <ZoomableGroup
                  zoom={mapPosition.zoom}
                  center={mapPosition.coordinates}
                  minZoom={1}
                  maxZoom={5}
                  onMoveEnd={(pos) => {
                    // Clamp: never zoom out below 1 (default frame)
                    const z = Math.max(1, pos.zoom);
                    // If at default zoom, reset center to prevent drift
                    if (z <= 1.05) {
                      setMapPosition({ coordinates: [0, 30], zoom: 1 });
                    } else {
                      // Clamp coordinates to world bounds
                      const lng = Math.max(-180, Math.min(180, pos.coordinates[0]));
                      const lat = Math.max(-60, Math.min(85, pos.coordinates[1]));
                      setMapPosition({ coordinates: [lng, lat], zoom: z });
                    }
                  }}
                  translateExtent={[[-200, -100], [1200, 700]]}
                >
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) => {
                      // Founder exclusive gold color palette (same as Dashboard WorldMap)
                      const founderGoldPalette = [
                        "#D4AF37", // Classic Gold
                        "#FFD700", // Pure Gold
                        "#C5A028", // Deep Gold
                        "#E6BE44", // Bright Gold
                        "#B8860B", // Dark Goldenrod
                        "#DAA520", // Goldenrod
                        "#F0C420", // Light Gold
                        "#CFAA3E", // Antique Gold
                      ];
                      
                      // Multi-color palette for non-founders
                      const colorPalette = [
                        "#E07A5F", // Terracotta
                        "#81B29A", // Sage green
                        "#F2994A", // Orange
                        "#9B51E0", // Purple
                        "#2D9CDB", // Blue
                        "#27AE60", // Green
                        "#EB5757", // Red
                        "#F2C94C", // Yellow
                      ];
                      
                      // Determine if using gold theme
                      const isGoldTheme = profile.founding_explorer && useGoldTheme;
                      
                      const countryColorMap = {};
                      
                      // Assign colors to visited countries using hash for consistency
                      profile.visited_country_codes.forEach((code) => {
                        const numericId = COUNTRY_NAME_TO_ID[code] || COUNTRY_NAME_TO_ID[code?.toUpperCase()];
                        if (numericId) {
                          // Use hash of country code for consistent color
                          const hash = code.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
                          const palette = isGoldTheme ? founderGoldPalette : colorPalette;
                          countryColorMap[numericId] = palette[hash % palette.length];
                        }
                      });
                      
                      return geographies.map((geo) => {
                        const isVisited = visitedCountryIds.has(geo.id);
                        
                        let fillColor;
                        if (isVisited) {
                          fillColor = countryColorMap[geo.id] || (isGoldTheme ? "#FFD700" : "#E07A5F");
                        } else {
                          // Gold theme: dark gray, Default: light beige
                          fillColor = isGoldTheme ? "#1a1a2e" : "#E8E4D9";
                        }
                        
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={fillColor}
                            stroke={isGoldTheme ? "#2a2a3a" : "#9CA3AF"}
                            strokeWidth={isGoldTheme ? 0.5 : 0.3}
                            style={{
                              default: { outline: "none" },
                              hover: { outline: "none", fill: isVisited ? undefined : (isGoldTheme ? "#1a1a2e" : "#D1D5DB") },
                              pressed: { outline: "none" },
                            }}
                          />
                        );
                      });
                    }}
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {profile.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-card rounded-xl p-4 text-center shadow-sm border border-border">
              <Plane className={`w-5 h-5 mx-auto mb-1 ${rankTextColor}`} />
              <div className="text-xl font-bold text-foreground">
                {profile.stats.completed_items}
              </div>
              <div className="text-xs text-muted-foreground">Adventures</div>
            </div>
            <div className="bg-card rounded-xl p-4 text-center shadow-sm border border-border">
              <Globe className={`w-5 h-5 mx-auto mb-1 ${rankTextColor}`} />
              <div className="text-xl font-bold text-foreground">
                {profile.stats.countries_visited}
              </div>
              <div className="text-xs text-muted-foreground">Countries</div>
            </div>
            <div className="bg-card rounded-xl p-4 text-center shadow-sm border border-border">
              <Award className="w-5 h-5 mx-auto text-yellow-500 mb-1" />
              <div className="text-xl font-bold text-foreground">
                {profile.badges?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">Badges</div>
            </div>
            <div className="bg-card rounded-xl p-4 text-center shadow-sm border border-border">
              <Flame className="w-5 h-5 mx-auto text-orange-500 mb-1" />
              <div className="text-xl font-bold text-foreground">
                {profile.streak?.longest || 0}
              </div>
              <div className="text-xs text-muted-foreground">Best Streak</div>
            </div>
          </div>
        )}

        {/* Founding Explorer Badge Section */}
        {profile.founding_explorer && (
          <div className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 dark:from-amber-500/20 dark:via-yellow-500/20 dark:to-amber-500/20 border border-amber-400/30 rounded-2xl p-5 mb-6 shadow-[0_0_20px_rgba(251,191,36,0.15)]">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-amber-700 dark:text-amber-400 text-lg">
                  Founding Explorer #{profile.founding_explorer_number}
                </h3>
                <p className="text-muted-foreground text-sm">
                  One of the original 200 pioneers of DuoLyst
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Streak Card */}
        {profile.streak && profile.streak.longest > 0 && (
          <div className="bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 dark:from-orange-500/20 dark:via-red-500/20 dark:to-orange-500/20 border border-orange-400/30 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 via-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-orange-700 dark:text-orange-400 text-lg">
                  {profile.streak.longest} Day Streak
                </h3>
                <p className="text-muted-foreground text-sm">
                  {profile.streak.current > 0 
                    ? `Currently on a ${profile.streak.current} day streak!`
                    : "Personal best login streak"}
                </p>
              </div>
              {profile.streak.longest >= 7 && (
                <div className="text-3xl">
                  {profile.streak.longest >= 100 ? "💎" : 
                   profile.streak.longest >= 60 ? "🌟" : 
                   profile.streak.longest >= 30 ? "👑" : 
                   profile.streak.longest >= 14 ? "⭐" : 
                   profile.streak.longest >= 7 ? "🏆" : "🔥"}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Achievement Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Achievement Badges ({profile.badges.length})
            </h2>
            {/* On mobile: horizontal scrolling row to keep section compact.
                On desktop / when "Show all" is tapped: wrap multi-row grid. */}
            <div
              className={
                showAllBadges
                  ? "flex flex-wrap gap-2"
                  : "flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 snap-x scrollbar-thin"
              }
              data-testid="public-profile-badges"
            >
              {(showAllBadges ? profile.badges : profile.badges.slice(0, 12)).map((badge) => (
                <div
                  key={badge.badge_id || badge.name}
                  className={`shrink-0 snap-start flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${
                    TIER_COLORS[badge.tier] || TIER_COLORS.bronze
                  } text-white text-xs sm:text-sm shadow-md whitespace-nowrap`}
                  title={badge.name}
                >
                  <span className="text-base">{badge.icon}</span>
                  <span className="font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
            {profile.badges.length > 12 && (
              <button
                type="button"
                onClick={() => setShowAllBadges((v) => !v)}
                className="mt-3 text-xs sm:text-sm text-primary hover:underline font-medium"
                data-testid="public-profile-badges-toggle"
              >
                {showAllBadges
                  ? "Show fewer"
                  : `Show all ${profile.badges.length} badges`}
              </button>
            )}
          </div>
        )}

        {/* Countries Visited */}
        {profile.countries_visited && profile.countries_visited.length > 0 && (
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <MapPin className={`w-5 h-5 ${rankTextColor}`} />
              Countries Explored ({profile.countries_visited.length})
            </h2>
            <div className="flex flex-wrap gap-2">
              {profile.countries_visited.map((country) => (
                <Badge
                  key={country.code || country.name}
                  variant="outline"
                  className="px-3 py-1 text-sm bg-card"
                >
                  {country.name}
                  {country.items_count > 1 && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({country.items_count})
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Recent Adventures */}
        {profile.recent_adventures && profile.recent_adventures.length > 0 && (
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Recent Adventures
              </h2>
              <div className="flex items-center gap-2">
                {isOwnProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/dashboard")}
                    className="text-primary"
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    Add Photos
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
                  className="text-muted-foreground"
                >
                  <ArrowUpDown className="w-4 h-4 mr-1" />
                  {sortOrder === "desc" ? "Newest" : "Oldest"}
                </Button>
              </div>
            </div>
            {/* Scrollable list after 7 items */}
            <div className={sortedAdventures.length > 7 ? "max-h-[420px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent" : ""}>
              <div className="space-y-3">
                {sortedAdventures.map((item) => (
                <div
                  key={item.id || item.title}
                  className="p-3 bg-muted/30 dark:bg-muted/10 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleTripClick(item)}
                >
                  <div className="flex items-center gap-3">
                    {item.photo_urls && item.photo_urls.length > 0 ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={item.photo_urls[0].thumbnail_url || item.photo_urls[0].url} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          CATEGORY_COLORS[item.category] || CATEGORY_COLORS.experiences
                        }`}
                      >
                        <Plane className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {item.title}
                      </p>
                      {item.location && (
                        <p className="text-sm text-muted-foreground truncate">
                          {item.location}
                          {item.country && `, ${item.country}`}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs text-muted-foreground block">
                        {new Date(item.target_date || item.completed_at).toLocaleDateString()}
                      </span>
                      {item.photo_urls && item.photo_urls.length > 0 && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Image className="w-3 h-3" /> {item.photo_urls.length}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        )}

        {/* Trip Detail Modal */}
        {selectedTrip && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedTrip(null)}>
            <div className="bg-card rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">{selectedTrip.title}</h3>
                    {selectedTrip.location && (
                      <p className="text-muted-foreground">{selectedTrip.location}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedTrip.target_date || selectedTrip.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedTrip(null)}
                    className="w-9 h-9 rounded-full bg-muted/60 flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0"
                    data-testid="trip-modal-close"
                  >
                    <X className="w-5 h-5 text-foreground" />
                  </button>
                </div>

                {/* Trip Photos */}
                {selectedTrip.photo_urls && selectedTrip.photo_urls.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {selectedTrip.photo_urls.map((photo) => (
                      <img 
                        key={photo.id}
                        src={photo.url} 
                        alt="Trip"
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}

                {/* Trip Reactions */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {["👍", "❤️", "🎉", "✈️", "🌍", "🔥"].map(emoji => {
                    const myTripReaction = getMyTripReaction(selectedTrip.id);
                    return (
                      <button
                        key={emoji}
                        onClick={() => handleTripReaction(selectedTrip.id, emoji)}
                        className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 transition-colors ${
                          myTripReaction === emoji
                            ? "bg-primary/20 ring-2 ring-primary"
                            : "bg-muted hover:bg-muted/80"
                        }`}
                      >
                        {emoji} {tripReactions[emoji] || 0}
                      </button>
                    );
                  })}
                </div>

                {/* Trip Comments */}
                <div className="border-t border-border pt-4">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> Comments ({tripComments.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                    {tripComments.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Be the first to comment!</p>
                    ) : (
                      tripComments.map(comment => (
                        <div key={comment.id} className="p-2 bg-muted/30 rounded-lg">
                          <p className="text-sm text-foreground">{comment.text}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            — {comment.author_name} · {new Date(comment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Comment Input */}
                  <form onSubmit={handleAddTripComment} className="space-y-2">
                    <input
                      type="text"
                      placeholder="Your name (optional)"
                      value={tripCommentAuthor}
                      onChange={(e) => setTripCommentAuthor(e.target.value)}
                      className="w-full p-2 rounded-lg bg-muted border-0 text-foreground text-sm placeholder:text-muted-foreground"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Write a comment..."
                        value={newTripComment}
                        onChange={(e) => setNewTripComment(e.target.value)}
                        className="flex-1 p-2 rounded-lg bg-muted border-0 text-foreground placeholder:text-muted-foreground"
                        data-testid="trip-comment-input"
                      />
                      <Button type="submit" size="sm" disabled={submittingTripComment || !newTripComment.trim()} className="px-3">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Reactions */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6">
          <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-pink-500" />
            Show Some Love
          </h2>
          <div className="flex flex-wrap gap-2">
            {["👍", "❤️", "🎉", "✈️", "🌍", "🔥", "⭐", "👏"].map(emoji => (
              <button
                key={emoji}
                onClick={() => handleReaction(emoji)}
                className={`px-4 py-2 rounded-full text-lg flex items-center gap-2 transition-colors ${
                  myProfileReaction === emoji 
                    ? "bg-primary/20 ring-2 ring-primary" 
                    : "bg-muted hover:bg-muted/80"
                }`}
              >
                {emoji} <span className="text-sm text-muted-foreground">{reactions[emoji] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6">
          <button
            onClick={() => setShowComments(!showComments)}
            className="w-full flex items-center justify-between font-semibold text-foreground"
          >
            <span className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              Comments ({comments.length})
            </span>
            {showComments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showComments && (
            <div className="mt-4">
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="mb-4">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  className="w-full p-2 mb-2 rounded-lg bg-muted border-0 text-foreground text-sm"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Leave a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1 p-2 rounded-lg bg-muted border-0 text-foreground"
                  />
                  <Button type="submit" disabled={submittingComment || !newComment.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
              
              {/* Comments List */}
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {comments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Be the first to comment!
                  </p>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-foreground">{comment.text}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        — {comment.author_name} · {new Date(comment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">
            Create your own travel bucket list!
          </p>
          <Link to="/register">
            <Button className={`bg-gradient-to-r ${rankColor} hover:opacity-90 text-white shadow-lg`}>
              Start Your List
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer with rank-themed accent */}
      <footer className="border-t border-border py-6 mt-12">
        <div className={`h-0.5 bg-gradient-to-r ${rankColor} opacity-30 mb-6`} />
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">
            DuoLyst
          </Link>
          {" · "}
          <span>Your Bucket List, Together</span>
        </div>
      </footer>
    </div>
    </RankThemeProvider>
  );
};

export default PublicProfile;
